import { EditGroupMode, OpportunityReviewBoardGroupDetails } from '@models/OpportunityReview';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { IRootState } from '@state/reducers/Reducers';
import { CATEGORY_FEATURE_OPTION } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { FeatureOption } from '@utils/pageConfig/Types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  OpportunityReviewBoardGroupContext,
  OpportunityReviewBoardGroupType,
} from './OpportunityReviewBoardGroupContext';

const OpportunityReviewBoardGroupProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);

  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [isCreateModalGroupPopUp, setIsCreateModalGroupPopUp] = useState<boolean>(false);
  const [groupNameToBeDelete, setGroupNameToBeDelete] = useState<string>('');
  const [isDeleteModalGroupPopUp, setIsDeleteModalGroupPopUp] = useState<boolean>(false);
  const [currentSelectedFeature, setCurrentSelectedFeature] = useState<FeatureOption>(CATEGORY_FEATURE_OPTION);
  const [isShareModalGroupPopUp, setIsShareModalGroupPopUp] = useState<boolean>(false);

  useEffect(() => {
    setCurrentSelectedFeature(mainFiltersState.feature);
  }, [mainFiltersState.feature]);

  const groupDetails: OpportunityReviewBoardGroupDetails = {
    groupName: '',
    groupElement: [],
    sortOrder: undefined,
  };

  const initialEditGroupMode: EditGroupMode = {
    groupDetails: groupDetails,
    isEditMode: false,
  };

  const [isEditGroupMode, setIsEditGroupMode] = useState<EditGroupMode>(initialEditGroupMode);

  const selectSegments = (segment: string) => {
    if (isSegmentsSelected(segment)) {
      setSelectedSegments(selectedSegments.filter((selectedSegment) => selectedSegment !== segment));
    } else {
      setSelectedSegments([...selectedSegments, segment]);
    }
  };

  const clearSelectedSegment = () => setSelectedSegments([]);

  const isSegmentsSelected = (segment: string) => selectedSegments.includes(segment);

  const setIsCreateModalGroupShown = (value: boolean) => {
    setIsCreateModalGroupPopUp(value);
  };

  const setIsDeleteModalGroupShown = (value: boolean) => {
    setIsDeleteModalGroupPopUp(value);
  };

  const setIsShareModalGroupShown = (value: boolean) => {
    setIsShareModalGroupPopUp(value);
  };

  const selectGroupNameToBeDeleted = (groupName: string) => {
    setGroupNameToBeDelete(groupName);
  };
  const resetGroupingState = () => {
    clearSelectedSegment();
    setIsCreateModalGroupPopUp(false);
    setGroupNameToBeDelete('');
    setIsDeleteModalGroupPopUp(false);
    setIsEditGroupMode(initialEditGroupMode);
  };
  const contextValue: OpportunityReviewBoardGroupType = useMemo(
    () => ({
      selectedSegments: selectedSegments,
      selectSegments: selectSegments,
      clearSelectedSegments: clearSelectedSegment,
      isSegmentsSelected: isSegmentsSelected,
      setSelectedSegments: setSelectedSegments,
      isCreateModalGroupPopUp: isCreateModalGroupPopUp,
      setIsCreateModalGroupPopUp: setIsCreateModalGroupShown,
      groupNameToBeDelete: groupNameToBeDelete,
      setGroupNameToBeDelete: selectGroupNameToBeDeleted,
      isDeleteModalGroupPopUp: isDeleteModalGroupPopUp,
      setIsDeleteModalGroupPopUp: setIsDeleteModalGroupShown,
      isShareModalGroupPopUp: isShareModalGroupPopUp,
      setIsShareModalGroupPopUp: setIsShareModalGroupShown,
      isEditGroupMode: isEditGroupMode,
      setIsEditGroupMode: setIsEditGroupMode,
      resetGroupingState: resetGroupingState,
      currentSelectedFeature: currentSelectedFeature,
      setCurrentSelectedFeature: setCurrentSelectedFeature,
    }),
    [
      selectedSegments,
      selectSegments,
      clearSelectedSegment,
      isSegmentsSelected,
      isCreateModalGroupPopUp,
      setIsCreateModalGroupShown,
      groupNameToBeDelete,
      selectGroupNameToBeDeleted,
      isDeleteModalGroupPopUp,
      setIsDeleteModalGroupShown,
      isShareModalGroupPopUp,
      setIsShareModalGroupShown,
      isEditGroupMode,
      setIsEditGroupMode,
      resetGroupingState,
      currentSelectedFeature,
      setCurrentSelectedFeature,
    ],
  );
  return (
    <OpportunityReviewBoardGroupContext.Provider value={contextValue}>
      {children}
    </OpportunityReviewBoardGroupContext.Provider>
  );
};

export default OpportunityReviewBoardGroupProvider;
